import React, { useState } from 'react';
import { Users, UserPlus, Shield, Eye, Search as SearchIcon, X, ChevronDown, Trash2, Crown } from 'lucide-react';
interface Member {
  id: string;
  name: string;
  email: string;
  initials: string;
  role: 'Admin' | 'Analyst' | 'Responder' | 'Viewer';
}
interface Team {
  id: number;
  name: string;
  description: string;
  members: Member[];
}
const roleColors: Record<string, string> = {
  Admin: 'bg-astro-green/10 text-astro-green border-astro-green/20',
  Analyst: 'bg-white/5 text-white border-white/10',
  Responder: 'bg-white/5 text-astro-text-secondary border-white/10',
  Viewer: 'bg-white/5 text-astro-text-secondary/70 border-white/5'
};
const roleIcons: Record<string, React.ReactNode> = {
  Admin: <Crown size={12} data-id="element-2286" />,
  Analyst: <Shield size={12} data-id="element-2287" />,
  Responder: <Users size={12} data-id="element-2288" />,
  Viewer: <Eye size={12} data-id="element-2289" />
};
const initialTeams: Team[] = [{
  id: 1,
  name: 'SOC Team Alpha',
  description: '24/7 Security Operations Center monitoring and response',
  members: [{
    id: 'm1',
    name: 'Sarah Chen',
    email: 'sarah.chen@astrolayb.com',
    initials: 'SC',
    role: 'Admin'
  }, {
    id: 'm2',
    name: 'James Wilson',
    email: 'j.wilson@astrolayb.com',
    initials: 'JW',
    role: 'Analyst'
  }, {
    id: 'm3',
    name: 'Priya Patel',
    email: 'p.patel@astrolayb.com',
    initials: 'PP',
    role: 'Analyst'
  }, {
    id: 'm4',
    name: 'Alex Rivera',
    email: 'a.rivera@astrolayb.com',
    initials: 'AR',
    role: 'Responder'
  }, {
    id: 'm5',
    name: 'Maya Kim',
    email: 'm.kim@astrolayb.com',
    initials: 'MK',
    role: 'Responder'
  }, {
    id: 'm6',
    name: 'David Lee',
    email: 'd.lee@astrolayb.com',
    initials: 'DL',
    role: 'Viewer'
  }]
}];
export function TeamsPage() {
  const [teams, setTeams] = useState<Team[]>(initialTeams);
  const [selectedTeamId, setSelectedTeamId] = useState<number>(teams[0]?.id ?? 0);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [newTeamName, setNewTeamName] = useState('');
  const [newTeamDesc, setNewTeamDesc] = useState('');
  const [newMemberName, setNewMemberName] = useState('');
  const [newMemberEmail, setNewMemberEmail] = useState('');
  const [newMemberRole, setNewMemberRole] = useState<Member['role']>('Analyst');
  const [roleFilter, setRoleFilter] = useState<Member['role'] | 'All'>('All');
  const selectedTeam = teams.find(t => t.id === selectedTeamId);
  const filteredMembers = selectedTeam ? roleFilter === 'All' ? selectedTeam.members : selectedTeam.members.filter(m => m.role === roleFilter) : [];
  const handleCreateTeam = () => {
    if (!newTeamName.trim()) return;
    const newTeam: Team = {
      id: Date.now(),
      name: newTeamName.trim(),
      description: newTeamDesc.trim() || 'New security team',
      members: []
    };
    setTeams(prev => [...prev, newTeam]);
    setSelectedTeamId(newTeam.id);
    setNewTeamName('');
    setNewTeamDesc('');
    setShowCreateModal(false);
  };
  const handleRemoveMember = (memberId: string) => {
    setTeams(prev => prev.map(t => t.id === selectedTeamId ? {
      ...t,
      members: t.members.filter(m => m.id !== memberId)
    } : t));
  };
  const handleChangeRole = (memberId: string, newRole: Member['role']) => {
    setTeams(prev => prev.map(t => t.id === selectedTeamId ? {
      ...t,
      members: t.members.map(m => m.id === memberId ? {
        ...m,
        role: newRole
      } : m)
    } : t));
  };
  const handleAddMember = () => {
    if (!newMemberName.trim() || !newMemberEmail.trim()) return;
    const nameParts = newMemberName.trim().split(' ');
    const initials = nameParts.length >= 2 ? (nameParts[0][0] + nameParts[nameParts.length - 1][0]).toUpperCase() : newMemberName.trim().slice(0, 2).toUpperCase();
    const newMember: Member = {
      id: 'm-' + Date.now(),
      name: newMemberName.trim(),
      email: newMemberEmail.trim(),
      initials,
      role: newMemberRole
    };
    setTeams(prev => prev.map(t => t.id === selectedTeamId ? {
      ...t,
      members: [...t.members, newMember]
    } : t));
    setNewMemberName('');
    setNewMemberEmail('');
    setNewMemberRole('Analyst');
    setShowAddMemberModal(false);
  };
  return <div className="max-w-7xl mx-auto px-6 py-8" data-id="element-2290">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8" data-id="element-2291">
        <div data-id="element-2292">
          <h1 className="text-3xl font-bold text-white mb-2" data-id="element-2293">Teams</h1>
          <p className="text-astro-text-secondary text-lg" data-id="element-2294">
            Manage your security teams and assign roles
          </p>
        </div>
        <button onClick={() => setShowCreateModal(true)} className="flex items-center gap-2 px-4 py-2 bg-astro-green-dark hover:bg-astro-green text-white text-sm font-medium rounded transition-colors shadow-[0_0_15px_rgba(34,197,94,0.15)]" data-id="element-2295">

          <UserPlus size={16} data-id="element-2296" />
          Create Team
        </button>
      </div>

      {/* Team Selector Tabs */}
      {teams.length > 1 && <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2" data-id="element-2297">
          {teams.map(team => <button key={team.id} onClick={() => setSelectedTeamId(team.id)} className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${selectedTeamId === team.id ? 'bg-astro-green/15 text-astro-green border border-astro-green/30' : 'text-astro-text-secondary hover:text-white border border-transparent hover:border-astro-border'}`} data-id="element-2298">

              {team.name}
              <span className="ml-2 text-xs opacity-60" data-id="element-2299">
                {team.members.length}
              </span>
            </button>)}
        </div>}

      {selectedTeam && <>
          {/* Team Info Card */}
          <div className="bg-astro-card border border-astro-border rounded-lg p-6 mb-6" data-id="element-2300">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4" data-id="element-2301">
              <div className="flex items-center gap-4" data-id="element-2302">
                <div className="w-14 h-14 rounded-lg bg-astro-green/10 flex items-center justify-center text-astro-green shrink-0" data-id="element-2303">
                  <Users size={28} data-id="element-2304" />
                </div>
                <div data-id="element-2305">
                  <h2 className="text-2xl font-bold text-white" data-id="element-2306">
                    {selectedTeam.name}
                  </h2>
                  <p className="text-sm text-astro-text-secondary mt-1" data-id="element-2307">
                    {selectedTeam.description}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4 text-sm text-astro-text-secondary" data-id="element-2308">
                <div className="text-center" data-id="element-2309">
                  <div className="text-xl font-mono font-bold text-white" data-id="element-2310">
                    {selectedTeam.members.length}
                  </div>
                  <div className="text-[10px] uppercase tracking-wider" data-id="element-2311">
                    Members
                  </div>
                </div>
                <div className="w-px h-10 bg-astro-border" data-id="element-2312"></div>
                <div className="text-center" data-id="element-2313">
                  <div className="text-xl font-mono font-bold text-astro-green" data-id="element-2314">
                    {selectedTeam.members.filter(m => m.role === 'Admin').length}
                  </div>
                  <div className="text-[10px] uppercase tracking-wider" data-id="element-2315">
                    Admins
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Members List */}
          <div className="bg-astro-card border border-astro-border rounded-lg overflow-hidden" data-id="element-2316">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 border-b border-astro-border gap-3" data-id="element-2317">
              <h3 className="font-bold text-white" data-id="element-2318">Team Members</h3>
              <div className="flex items-center gap-2 sm:gap-4 flex-wrap" data-id="element-2319">
                <div className="flex items-center gap-1 overflow-x-auto" data-id="element-2320">
                  <button onClick={() => setRoleFilter('All')} className={`px-2 py-1 rounded text-xs font-medium transition-all whitespace-nowrap ${roleFilter === 'All' ? 'bg-astro-green/15 text-astro-green border border-astro-green/30' : 'text-astro-text-secondary hover:text-white border border-transparent'}`} data-id="element-2321">

                    All
                    <span className="ml-1 opacity-60" data-id="element-2322">
                      {selectedTeam?.members.length}
                    </span>
                  </button>
                  {(['Admin', 'Analyst', 'Responder', 'Viewer'] as const).map(role => {
                const count = selectedTeam?.members.filter(m => m.role === role).length ?? 0;
                return <button key={role} onClick={() => setRoleFilter(roleFilter === role ? 'All' : role)} className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-all whitespace-nowrap ${roleFilter === role ? 'bg-astro-green/15 text-astro-green border border-astro-green/30' : 'text-astro-text-secondary hover:text-white border border-transparent'}`} data-id="element-2323">

                          {roleIcons[role]}
                          <span className="hidden sm:inline" data-id="element-2324">{role}</span>
                          <span className="sm:hidden" data-id="element-2325">{role[0]}</span>
                          <span className="opacity-60" data-id="element-2326">{count}</span>
                        </button>;
              })}
                </div>
                <button onClick={() => setShowAddMemberModal(true)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-astro-green border border-astro-green/30 rounded hover:bg-astro-green/10 transition-colors whitespace-nowrap" data-id="element-2327">

                  <UserPlus size={14} data-id="element-2328" />
                  <span className="hidden sm:inline" data-id="element-2329">Add Member</span>
                  <span className="sm:hidden" data-id="element-2330">Add</span>
                </button>
              </div>
            </div>

            {filteredMembers.length === 0 && selectedTeam && selectedTeam.members.length > 0 ? <div className="p-12 text-center text-astro-text-secondary" data-id="element-2331">
                <Users size={32} className="mx-auto mb-3 opacity-40" data-id="element-2332" />
                <p className="text-sm" data-id="element-2333">
                  No{' '}
                  {roleFilter !== 'All' ? roleFilter.toLowerCase() + 's' : 'members'}{' '}
                  found.
                </p>
                <button onClick={() => setRoleFilter('All')} className="text-xs text-astro-green hover:text-astro-green-dark mt-2 transition-colors" data-id="element-2334">

                  Clear filter
                </button>
              </div> : filteredMembers.length === 0 ? <div className="p-12 text-center text-astro-text-secondary" data-id="element-2335">
                <Users size={32} className="mx-auto mb-3 opacity-40" data-id="element-2336" />
                <p className="text-sm" data-id="element-2337">
                  No members yet. Add team members to get started.
                </p>
              </div> : <div className="divide-y divide-astro-border" data-id="element-2338">
                {filteredMembers.map(member => <div key={member.id} className="flex items-center justify-between p-4 hover:bg-white/[0.02] transition-colors" data-id="element-2339">

                    <div className="flex items-center gap-3" data-id="element-2340">
                      <div className="w-9 h-9 rounded-full bg-astro-green-dim border border-astro-border flex items-center justify-center" data-id="element-2341">
                        <span className="text-xs font-bold text-astro-green" data-id="element-2342">
                          {member.initials}
                        </span>
                      </div>
                      <div data-id="element-2343">
                        <div className="text-sm font-medium text-white" data-id="element-2344">
                          {member.name}
                        </div>
                        <div className="text-xs text-astro-text-secondary" data-id="element-2345">
                          {member.email}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-3" data-id="element-2346">
                      {/* Role Selector */}
                      <div className="relative" data-id="element-2347">
                        <select value={member.role} onChange={e => handleChangeRole(member.id, e.target.value as Member['role'])} className={`appearance-none pl-2 pr-7 py-1 rounded text-[11px] font-bold uppercase tracking-wider border cursor-pointer focus:outline-none ${roleColors[member.role]}`} data-id="element-2348">

                          <option value="Admin" data-id="element-2349">Admin</option>
                          <option value="Analyst" data-id="element-2350">Analyst</option>
                          <option value="Responder" data-id="element-2351">Responder</option>
                          <option value="Viewer" data-id="element-2352">Viewer</option>
                        </select>
                        <ChevronDown size={10} className="absolute right-1.5 top-1/2 -translate-y-1/2 pointer-events-none opacity-50" data-id="element-2353" />

                      </div>

                      {/* Remove */}
                      <button onClick={() => handleRemoveMember(member.id)} className="p-1.5 text-astro-text-secondary/50 hover:text-astro-danger transition-colors" title="Remove member" data-id="element-2354">

                        <Trash2 size={14} data-id="element-2355" />
                      </button>
                    </div>
                  </div>)}
              </div>}
          </div>
        </>}

      {/* Create Team Modal */}
      {showCreateModal && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-id="element-2356">
          <div className="w-full max-w-[460px] bg-astro-card border border-astro-border rounded-lg shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200" data-id="element-2357">
            <div className="flex items-center justify-between p-5 border-b border-astro-border" data-id="element-2358">
              <h2 className="text-lg font-bold text-white" data-id="element-2359">Create New Team</h2>
              <button onClick={() => setShowCreateModal(false)} className="text-astro-text-secondary hover:text-white transition-colors p-1" data-id="element-2360">

                <X size={20} data-id="element-2361" />
              </button>
            </div>

            <div className="p-6 space-y-4" data-id="element-2362">
              <div data-id="element-2363">
                <label className="block text-sm font-medium text-astro-text-secondary mb-1.5" data-id="element-2364">
                  Team Name
                </label>
                <input type="text" value={newTeamName} onChange={e => setNewTeamName(e.target.value)} placeholder="e.g. Incident Response" className="w-full bg-[#0a0e14] border border-astro-border rounded px-3 py-2 text-sm text-white placeholder:text-astro-text-secondary/50 focus:outline-none focus:border-astro-green/50 transition-colors" autoFocus data-id="element-2365" />

              </div>

              <div data-id="element-2366">
                <label className="block text-sm font-medium text-astro-text-secondary mb-1.5" data-id="element-2367">
                  Description
                </label>
                <textarea value={newTeamDesc} onChange={e => setNewTeamDesc(e.target.value)} placeholder="What does this team do?" rows={3} className="w-full bg-[#0a0e14] border border-astro-border rounded px-3 py-2 text-sm text-white placeholder:text-astro-text-secondary/50 focus:outline-none focus:border-astro-green/50 transition-colors resize-none" data-id="element-2368" />

              </div>

              <div className="flex justify-end gap-3 pt-2" data-id="element-2369">
                <button onClick={() => setShowCreateModal(false)} className="px-4 py-2 text-sm font-medium text-astro-text-secondary border border-astro-border rounded hover:text-white hover:border-white/30 transition-colors" data-id="element-2370">

                  Cancel
                </button>
                <button onClick={handleCreateTeam} disabled={!newTeamName.trim()} className="px-4 py-2 text-sm font-medium text-white bg-astro-green-dark hover:bg-astro-green rounded transition-colors shadow-[0_0_15px_rgba(34,197,94,0.15)] disabled:opacity-40 disabled:cursor-not-allowed" data-id="element-2371">

                  Create Team
                </button>
              </div>
            </div>
          </div>
        </div>}

      {/* Add Member Modal */}
      {showAddMemberModal && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-id="element-2372">
          <div className="w-full max-w-[460px] bg-astro-card border border-astro-border rounded-lg shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200" data-id="element-2373">
            <div className="flex items-center justify-between p-5 border-b border-astro-border" data-id="element-2374">
              <h2 className="text-lg font-bold text-white" data-id="element-2375">Add Team Member</h2>
              <button onClick={() => setShowAddMemberModal(false)} className="text-astro-text-secondary hover:text-white transition-colors p-1" data-id="element-2376">

                <X size={20} data-id="element-2377" />
              </button>
            </div>

            <div className="p-6 space-y-4" data-id="element-2378">
              <div data-id="element-2379">
                <label className="block text-sm font-medium text-astro-text-secondary mb-1.5" data-id="element-2380">
                  Full Name
                </label>
                <input type="text" value={newMemberName} onChange={e => setNewMemberName(e.target.value)} placeholder="e.g. Jordan Smith" className="w-full bg-[#0a0e14] border border-astro-border rounded px-3 py-2 text-sm text-white placeholder:text-astro-text-secondary/50 focus:outline-none focus:border-astro-green/50 transition-colors" autoFocus data-id="element-2381" />

              </div>

              <div data-id="element-2382">
                <label className="block text-sm font-medium text-astro-text-secondary mb-1.5" data-id="element-2383">
                  Email Address
                </label>
                <input type="email" value={newMemberEmail} onChange={e => setNewMemberEmail(e.target.value)} placeholder="e.g. j.smith@astrolayb.com" className="w-full bg-[#0a0e14] border border-astro-border rounded px-3 py-2 text-sm text-white placeholder:text-astro-text-secondary/50 focus:outline-none focus:border-astro-green/50 transition-colors" data-id="element-2384" />

              </div>

              <div data-id="element-2385">
                <label className="block text-sm font-medium text-astro-text-secondary mb-1.5" data-id="element-2386">
                  Role
                </label>
                <div className="grid grid-cols-4 gap-2" data-id="element-2387">
                  {(['Admin', 'Analyst', 'Responder', 'Viewer'] as const).map(role => <button key={role} onClick={() => setNewMemberRole(role)} className={`flex flex-col items-center gap-1 px-2 py-2.5 rounded border text-xs font-bold transition-all ${newMemberRole === role ? 'bg-astro-green/10 border-astro-green/30 text-astro-green' : 'bg-[#0a0e14] border-astro-border text-astro-text-secondary hover:text-white hover:border-white/20'}`} data-id="element-2388">

                        {roleIcons[role]}
                        {role}
                      </button>)}
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-2" data-id="element-2389">
                <button onClick={() => setShowAddMemberModal(false)} className="px-4 py-2 text-sm font-medium text-astro-text-secondary border border-astro-border rounded hover:text-white hover:border-white/30 transition-colors" data-id="element-2390">

                  Cancel
                </button>
                <button onClick={handleAddMember} disabled={!newMemberName.trim() || !newMemberEmail.trim()} className="px-4 py-2 text-sm font-medium text-white bg-astro-green-dark hover:bg-astro-green rounded transition-colors shadow-[0_0_15px_rgba(34,197,94,0.15)] disabled:opacity-40 disabled:cursor-not-allowed" data-id="element-2391">

                  Add Member
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}